import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Icons } from './Icons';
import MobileBottomSheet from './ui/MobileBottomSheet/MobileBottomSheet';
import MobileMoreMenu from './MobileMoreMenu/MobileMoreMenu';
import { useTranslation } from '../utils/i18n';
import sharedTranslations from './shared/translations';
import './BottomNavigation.css';

/**
 * BottomNavigation Component
 * Mobile-first bottom tab navigation for thumb-friendly access
 * Only visible on mobile devices (max-width: 768px)
 * 
 * Supports three variants:
 * - customer: Products, Payments, Marketplace, More
 * - technical: Dashboard, Tenants, Resources, More
 * - public: Home, Marketplace, About Us, More
 */
const BottomNavigation = ({ 
  variant = 'customer', // 'customer' | 'technical' | 'public'
  counts = {},
  onMoreClick, // Optional callback when More is clicked
  onSignIn // Optional: when provided (platform-public), passed to MobileMoreMenu for Sign in redirect
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useTranslation();
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  // Get shared translations
  const getSharedTranslation = (key, defaultValue = '') => {
    const translations = sharedTranslations[language] || sharedTranslations.en;
    const value = key.split('.').reduce((obj, k) => obj?.[k], translations);
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
    return defaultValue || key;
  };
  
  // Define tabs based on variant
  const getTabs = () => {
    switch (variant) {
      case 'customer':
        return [
          {
            id: 'products',
            label: getSharedTranslation('bottomNav.myProducts', 'Products'),
            icon: Icons.LayoutGrid,
            path: '/',
            count: counts.products || counts.resources
          },
          {
            id: 'payments',
            label: getSharedTranslation('bottomNav.payments', 'Payments'),
            icon: Icons.CreditCard,
            path: '/payment-profile',
            count: null
          },
          {
            id: 'marketplace',
            label: getSharedTranslation('bottomNav.marketplace', 'Marketplace'),
            icon: Icons.Store,
            path: '/marketplace',
            count: null
          },
          {
            id: 'more',
            label: getSharedTranslation('bottomNav.more', 'More'),
            icon: Icons.Menu,
            path: null, // Opens bottom sheet
            count: null
          }
        ];
      
      case 'technical':
        return [
          {
            id: 'dashboard',
            label: getSharedTranslation('bottomNav.dashboard', 'Dashboard'),
            icon: Icons.LayoutGrid,
            path: '/',
            count: null
          },
          {
            id: 'tenants',
            label: getSharedTranslation('bottomNav.tenants', 'Tenants'),
            icon: Icons.Building,
            path: '/?tab=tenants',
            count: counts.tenants
          },
          {
            id: 'resources',
            label: getSharedTranslation('bottomNav.resources', 'Resources'),
            icon: Icons.Sparkles,
            path: '/?tab=resources',
            count: counts.resources
          },
          {
            id: 'more',
            label: getSharedTranslation('bottomNav.more', 'More'),
            icon: Icons.Menu,
            path: null, // Opens bottom sheet
            count: null
          }
        ];
      
      case 'public':
      default:
        return [
          {
            id: 'home',
            label: getSharedTranslation('bottomNav.home', 'Home'),
            icon: Icons.Home,
            path: '/',
            count: null
          },
          {
            id: 'marketplace',
            label: getSharedTranslation('bottomNav.marketplace', 'Marketplace'),
            icon: Icons.Store,
            path: '/marketplace',
            count: null
          },
          {
            id: 'about',
            label: getSharedTranslation('bottomNav.about', 'About Us'),
            icon: Icons.Info,
            path: '/about',
            count: null
          },
          {
            id: 'more',
            label: getSharedTranslation('bottomNav.more', 'More'),
            icon: Icons.Menu,
            path: null, // Opens bottom sheet
            count: null
          }
        ];
    }
  };

  const tabs = getTabs();

  // Determine active tab based on current path
  const getActiveTab = () => {
    const path = location.pathname;
    
    if (variant === 'customer') {
      if (path === '/' || (path.startsWith('/en/') && path.length <= 4)) return 'products';
      if (path.includes('/payment-profile')) return 'payments';
      if (path.includes('/marketplace')) return 'marketplace';
      return 'products';
    }
    
    if (variant === 'technical') {
      if (path === '/' || (path.startsWith('/en/') && path.length <= 4)) {
        const tab = new URLSearchParams(location.search).get('tab');
        if (tab === 'tenants') return 'tenants';
        if (tab === 'resources') return 'resources';
        return 'dashboard';
      }
      return 'dashboard';
    }
    
    if (variant === 'public') {
      if (path === '/' || (path.startsWith('/en/') && path.length <= 4)) return 'home';
      if (path.includes('/marketplace')) return 'marketplace';
      if (path.includes('/about')) return 'about';
      return 'home';
    }
    
    return tabs[0]?.id || 'home';
  };

  const activeTab = getActiveTab();

  const handleTabClick = (tab) => {
    if (tab.id === 'more') {
      setShowMoreMenu(true);
      if (onMoreClick) {
        onMoreClick();
      }
    } else if (tab.path) {
      navigate(tab.path);
    }
  };

  const handleCloseMoreMenu = () => {
    setShowMoreMenu(false);
  };

  return (
    <>
      <nav className="bottom-navigation" role="navigation" aria-label="Main navigation">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          
          return (
            <button
              key={tab.id}
              className={`bottom-nav-item ${isActive ? 'active' : ''}`}
              onClick={() => handleTabClick(tab)}
              aria-label={tab.label}
              aria-current={isActive ? 'page' : undefined}
            >
              <div className="bottom-nav-icon-wrapper">
                <Icon size={22} />
                {tab.count !== null && tab.count !== undefined && tab.count > 0 && (
                  <span className="bottom-nav-badge">{tab.count}</span>
                )}
              </div>
              <span className="bottom-nav-label">{tab.label}</span>
            </button>
          );
        })}
      </nav>

      {/* More Menu Bottom Sheet */}
      <MobileBottomSheet
        isOpen={showMoreMenu}
        onClose={handleCloseMoreMenu}
        title={getSharedTranslation('bottomNav.more', 'More')}
      >
        <MobileMoreMenu 
          variant={variant}
          onLinkClick={handleCloseMoreMenu}
          onSignIn={onSignIn}
        />
      </MobileBottomSheet>
    </>
  );
};

export default BottomNavigation;


